/* eslint-disable @typescript-eslint/no-explicit-any */
import "./index.css";
import "./styles/tutorial.css";

import ReactDOM from "react-dom/client";
import { isStorageAvailable } from "./utils/storage-check";
import { recordChunkLoadFailure } from "./utils/chunk-reload-telemetry";
import StorageDisabledPage from "./pages/StorageDisabledPage";

// Inline blocked-resource fallback (see index.html). The early inline
// script defines window.__viralyShowFallback before this module runs;
// we keep a safe reference so the dynamic-import catch path below can
// surface the same panel even if the global is somehow absent.
declare global {
  interface Window {
    __viralyShowFallback?: () => void;
  }
}

// matchMedia shim (I11-MS / VIRALY-SPA-MS).
//
// A third-party consumer (seen on /oauth/twitter/callback) calls
// `window.matchMedia(...).addListener(...)` at module-load time. In a
// few embedded/in-app browser contexts `window.matchMedia` is missing
// or returns a falsy result, so `.addListener` throws and crashes the
// whole page before React mounts (blank #root). We guarantee a minimal
// MediaQueryList-shaped object that exposes BOTH the legacy
// addListener/removeListener API and the modern
// addEventListener/removeEventListener API, mirroring the SSR stub in
// prerender-polyfill.ts. Kept intentionally minimal (matches:false, no
// real evaluation) so it never masks genuine breakpoint behaviour where
// a real matchMedia exists.
if (typeof window !== "undefined") {
  const makeStub = (query: string): any => ({
    matches: false,
    media: typeof query === "string" ? query : "",
    onchange: null,
    addListener: () => {},
    removeListener: () => {},
    addEventListener: () => {},
    removeEventListener: () => {},
    dispatchEvent: () => false,
  });

  const native = window.matchMedia;
  if (typeof native !== "function") {
    (window as any).matchMedia = makeStub;
  } else {
    // matchMedia exists but can still return a falsy/incomplete result
    // in some embedded webviews - wrap it so callers always get an
    // object exposing the listener methods.
    (window as any).matchMedia = (query: string): any => {
      let result: any;
      try {
        result = native.call(window, query);
      } catch {
        result = null;
      }
      if (!result) return makeStub(query);
      if (typeof result.addListener !== "function") {
        result.addListener =
          typeof result.addEventListener === "function"
            ? (cb: any) => result.addEventListener("change", cb)
            : () => {};
      }
      if (typeof result.removeListener !== "function") {
        result.removeListener =
          typeof result.removeEventListener === "function"
            ? (cb: any) => result.removeEventListener("change", cb)
            : () => {};
      }
      if (typeof result.addEventListener !== "function") {
        result.addEventListener = () => {};
      }
      if (typeof result.removeEventListener !== "function") {
        result.removeEventListener = () => {};
      }
      return result;
    };
  }
}

// Patch DOM methods to handle Google Translate (and similar extensions) that
// modify the DOM by wrapping text nodes in <font> tags. When React later tries
// to remove or insert around those nodes, it fails with "NotFoundError: The
// node to be removed is not a child of this node." This silently catches that
// specific error so the UI doesn't break for translated pages.
if (typeof Node !== "undefined") {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      if (child.parentNode) {
        return originalRemoveChild.call(child.parentNode, child) as T;
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

// Bridge ResizeObserver -> window.resize event.
//
// Inside Shopify's App Bridge iframe (and other sandboxed cross-origin
// iframes), the iframe's `window` does not always emit `resize` events when
// the parent admin layout resizes the iframe - a long-standing Chromium quirk.
// This means `window.matchMedia` listeners (used by react-responsive, charts,
// drag-and-drop libs, etc.) get stuck on the iframe's initial dimensions and
// never re-evaluate, leaving the SPA stuck in mobile layout when the iframe
// is actually wide.
//
// ResizeObserver fires reliably in iframes, so we observe the document
// element and dispatch a synthetic resize event whenever its layout box
// changes. This re-triggers all listeners that depend on `window.resize`,
// including third-party libraries we don't control. The native `useBreakpoint`
// hook reads the dimension directly from `documentElement.clientWidth` so it
// doesn't need this bridge, but everything else does.
if (
  typeof window !== "undefined" &&
  typeof ResizeObserver !== "undefined" &&
  typeof document !== "undefined" &&
  document.documentElement
) {
  let lastWidth = document.documentElement.clientWidth;
  let lastHeight = document.documentElement.clientHeight;
  const observer = new ResizeObserver(() => {
    const w = document.documentElement.clientWidth;
    const h = document.documentElement.clientHeight;
    if (w === lastWidth && h === lastHeight) return;
    lastWidth = w;
    lastHeight = h;
    window.dispatchEvent(new Event("resize"));
  });
  observer.observe(document.documentElement);
}

if (!isStorageAvailable()) {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <StorageDisabledPage />,
  );
} else {
  (async () => {
    try {
      const {
        RouterProvider,
        router,
        initializeMetaPixel,
        FileRejectionAlertProvider,
        ThemeProvider,
        EmbeddedModeProvider,
        HelmetProvider,
      } = await import("./bootstrap");

      initializeMetaPixel().catch(console.error);

      ReactDOM.createRoot(document.getElementById("root")!).render(
        <HelmetProvider>
          <EmbeddedModeProvider>
            <ThemeProvider defaultTheme="system" storageKey="viraly-theme">
              <FileRejectionAlertProvider>
                <RouterProvider router={router} />
              </FileRejectionAlertProvider>
            </ThemeProvider>
          </EmbeddedModeProvider>
        </HelmetProvider>,
      );
    } catch (error) {
      // Dynamic import failure. Two distinct causes:
      //   1. Stale cached HTML after a deployment (old chunk URLs 404) -
      //      a one-time reload fetches fresh HTML and fixes it.
      //   2. A network filter (VPN / ad-blocker / threat protection) is
      //      blocking our own asset host outright - reloading won't help.
      // We try the reload once; if we've already reloaded and still
      // failed, render the richer blocked-resource fallback (defined
      // inline in index.html) that names the likely external causes.
      const hasReloaded = sessionStorage.getItem("chunk-reload");
      if (!hasReloaded) {
        sessionStorage.setItem("chunk-reload", "1");
        // This reload can silently wipe in-progress work (an open composer),
        // so it must be visible in Sentry. Sentry lives in the bundle that
        // just failed to load, so the capture cannot happen here; park the
        // details and let config/sentry.js send them on the next successful
        // boot.
        recordChunkLoadFailure(error);
        window.location.reload();
      } else {
        sessionStorage.removeItem("chunk-reload");
        if (typeof window.__viralyShowFallback === "function") {
          window.__viralyShowFallback();
        } else {
          // Defensive fallback if the inline index.html script didn't run.
          document.getElementById("root")!.innerHTML =
            '<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui;color:#6b7280;text-align:center;padding:2rem">' +
            "<div><p>We had trouble loading the app. This is often caused by a VPN, ad-blocker, or threat-protection filter blocking app.viraly.io. Please pause it and refresh.</p>" +
            '<button onclick="window.location.reload()" style="margin-top:1rem;padding:0.5rem 1.5rem;background:#4f46e5;color:white;border:none;border-radius:0.375rem;cursor:pointer;font-size:0.875rem">Refresh</button></div></div>';
        }
      }
    }
  })();
}
